import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listMailCleanupRuns, startMailCleanup, getMailCleanupSettings, saveMailCleanupSettings } from '../utils/api';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, Icon, Pager, usePager, useToast } from '../components/ui';

const DEFAULT_LIMIT = 26;
const MAX_LIMIT = 100;
// After Run cleanup now, the list is re-read this often until the new row shows up or we give up.
const WATCH_EVERY_MS = 5000;
const WATCH_FOR_MS = 3 * 60 * 1000;
// Retention (days of mail kept) is saved through the API and read by the cleanup Lambda at each
// run. Until an admin saves one the Lambda uses its stack default, which is this value.
const DEFAULT_RETENTION_DAYS = 7;
const MAX_RETENTION_DAYS = 365;

// The kit's fmtDate only handles YYYY-MM-DD; runs carry full timestamps.
function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function fmtDuration(ms) {
  if (ms == null) return '';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

// The Lambda records 'schedule' for the nightly run, 'dashboard' for Run cleanup now, 'manual' for a hand invoke.
const triggerLabel = (run) => {
  const t = (run.trigger || '').toLowerCase();
  if (t.startsWith('sched')) return 'Scheduled';
  if (t === 'dashboard') return 'Dashboard';
  return 'Manual';
};
const modeLabel = (run) => (run.dryRun ? 'Dry run' : 'Live');
const daysLabel = (n) => `${n} day${n === 1 ? '' : 's'}`;
const retentionLabel = (run) => (run.retentionDays > 0 ? `Kept the newest ${daysLabel(run.retentionDays)}` : 'Everything in the three folders');

// A network-level failure surfaces as "Failed to fetch", which means nothing to staff.
const loadError = (e, fallback) => (!e?.message || /failed to fetch/i.test(e.message)
  ? `${fallback}. Check your connection and try again.`
  : e.message);

export default function MailboxCleanupPage() {
  const [params, setParams] = useSearchParams();
  const openId = params.get('run') || '';
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [tick, setTick] = useState(0);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [toast, toastNode] = useToast();
  const [starting, setStarting] = useState(false);
  // Set by Run cleanup now: when it was pressed and the newest startedAt at that moment. While
  // set, the list is polled until a row newer than that appears (no reliance on the browser clock).
  const [watch, setWatch] = useState(null);
  const watchTimer = useRef(null);
  // Retention setting: what the API holds, the input's text, and a counter the Refresh button bumps.
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState('');
  const [settingsTick, setSettingsTick] = useState(0);
  const [retentionInput, setRetentionInput] = useState('');
  const [savingRetention, setSavingRetention] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { runs = [] } = await listMailCleanupRuns(limit);
        if (!cancelled) { setRows(runs); setError(''); }
      } catch (e) { if (!cancelled) { setError(loadError(e, 'Could not load the cleanup log')); setRows([]); } }
    })();
    return () => { cancelled = true; };
  }, [limit, tick]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getMailCleanupSettings();
        if (!cancelled) {
          setSettings(s);
          setRetentionInput(String(s.retentionDays == null ? DEFAULT_RETENTION_DAYS : s.retentionDays));
          setSettingsError('');
        }
      } catch (e) { if (!cancelled) setSettingsError(loadError(e, 'Could not load the retention setting')); }
    })();
    return () => { cancelled = true; };
  }, [settingsTick]);

  // Poll after Run cleanup now. The cleanup Lambda writes its row when it finishes, usually
  // within a minute; stop as soon as a row newer than the request appears.
  useEffect(() => {
    if (!watch) return undefined;
    const arrived = (rows || []).some(r => (r.startedAt || '') > watch.topBefore);
    if (arrived) { setWatch(null); toast('Cleanup finished. The new run is at the top of the list.'); return undefined; }
    if (Date.now() - watch.since > WATCH_FOR_MS) { setWatch(null); toast('The cleanup has not reported yet. Use Refresh in a minute.', 'error'); return undefined; }
    watchTimer.current = setTimeout(() => setTick(t => t + 1), WATCH_EVERY_MS);
    return () => clearTimeout(watchTimer.current);
  }, [rows, watch, toast]);

  // What the next run will keep: the saved setting, or the stack default until one is saved.
  const effectiveRetention = settings && settings.retentionDays != null ? settings.retentionDays : DEFAULT_RETENTION_DAYS;
  const retentionDirty = settings != null && retentionInput.trim() !== String(effectiveRetention);

  const saveRetention = async (e) => {
    e.preventDefault();
    if (savingRetention || !settings) return;
    const text = retentionInput.trim();
    const days = Number(text);
    if (!/^\d+$/.test(text) || days > MAX_RETENTION_DAYS) {
      toast(`Enter a whole number of days from 0 to ${MAX_RETENTION_DAYS}.`, 'error');
      return;
    }
    if (days === 0 && !window.confirm('Keep 0 days? Starting with the next run, every message in Inbox, Sent and Trash of every employee mailbox will be deleted. Deleted mail cannot be recovered.')) return;
    setSavingRetention(true);
    try {
      const saved = await saveMailCleanupSettings(days);
      setSettings(saved);
      setRetentionInput(String(saved.retentionDays));
      toast(days === 0 ? 'Saved. The next run deletes everything in the three folders.' : `Saved. The next run keeps the newest ${daysLabel(days)}.`);
    } catch (err) {
      toast(loadError(err, 'Could not save the retention'), 'error');
    } finally {
      setSavingRetention(false);
    }
  };

  const runNow = async () => {
    if (starting || watch) return;
    const scope = effectiveRetention > 0
      ? `It keeps the newest ${daysLabel(effectiveRetention)} and deletes the rest.`
      : 'Retention is 0 days, so every message in the three folders will be deleted.';
    if (!window.confirm(`Clean Inbox, Sent and Trash in every employee mailbox on email.homecareofmi.com now? ${scope} Deleted mail cannot be recovered.`)) return;
    setStarting(true);
    try {
      await startMailCleanup();
      setWatch({ since: Date.now(), topBefore: (rows && rows[0] && rows[0].startedAt) || '' });
      toast('Cleanup started. The result appears here in about a minute.');
      setTick(t => t + 1);
    } catch (e) {
      toast(loadError(e, 'Could not start the cleanup'), 'error');
    } finally {
      setStarting(false);
    }
  };

  const { pageRows, pagerProps } = usePager(rows || [], [limit]);

  const setParam = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  // The drawer is keyed by ?run=ID so a link can open it directly. Every run already
  // carries its per-mailbox counts, so no second request is needed.
  const detail = useMemo(() => (rows || []).find(r => r.runId === openId) || null, [rows, openId]);
  const openRun = (r) => setParam('run', r.runId);
  const closeRun = () => setParam('run', '');
  useEffect(() => {
    if (!openId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setParams(p => { const n = new URLSearchParams(p); n.delete('run'); return n; }, { replace: true }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openId, setParams]);

  const canShowMore = rows && rows.length >= limit && limit < MAX_LIMIT;

  return (
    <main className="main-content ops-main">
      <div className="ops-page">
        <PageHeader title="Mailbox Cleanup Log"
          subtitle="Every run that cleans Inbox, Sent and Trash for the employee mailboxes on email.homecareofmi.com: the nightly run at 3:00 AM Detroit time and any run started here. Click a row to see the count per mailbox."
          actions={<>
            <button type="button" className="ops-btn" onClick={() => { setTick(t => t + 1); setSettingsTick(t => t + 1); }}><Icon name="refresh" /> Refresh</button>
            <button type="button" className="ops-btn ops-btn--danger" onClick={runNow} disabled={starting || !!watch}>
              {starting ? 'Starting…' : watch ? 'Cleanup running…' : 'Run cleanup now'}
            </button>
          </>} />
        {toastNode}
        {error && <div className="ops-error">{error}</div>}
        <Panel title="Retention" className="ops-care-panel"
          action={<span className="ops-cell-sub">Applies to the next run, nightly or started here.</span>}>
          {settingsError && <div className="ops-error">{settingsError}</div>}
          <form className="ops-retention" onSubmit={saveRetention}>
            <div className="ops-field">
              <label htmlFor="retention-days">Days of mail to keep</label>
              <input id="retention-days" className="ops-input" type="number" inputMode="numeric" min={0} max={MAX_RETENTION_DAYS} step={1}
                value={retentionInput} onChange={e => setRetentionInput(e.target.value)} disabled={!settings || savingRetention} />
              <span className="ops-field__hint">Whole days from 0 to {MAX_RETENTION_DAYS}. 0 deletes every message in Inbox, Sent and Trash.</span>
            </div>
            <button type="submit" className="ops-btn ops-btn--primary" disabled={!settings || savingRetention || !retentionDirty}>
              {savingRetention ? 'Saving…' : 'Save'}
            </button>
            <div className="ops-cell-sub ops-retention__status">
              {!settings ? (settingsError ? '' : 'Loading…')
                : settings.retentionDays == null ? `Nothing saved yet. Runs keep the newest ${daysLabel(DEFAULT_RETENTION_DAYS)}, the stack default.`
                : <>
                    {settings.retentionDays > 0 ? `Runs keep the newest ${daysLabel(settings.retentionDays)}.` : 'Runs delete every message in Inbox, Sent and Trash.'}
                    {settings.updatedAt ? ` Set${settings.updatedBy ? ` by ${settings.updatedBy}` : ''} on ${fmtDateTime(settings.updatedAt)}.` : ''}
                  </>}
            </div>
          </form>
        </Panel>
        <Panel flush title="Runs" count={rows ? rows.length : undefined}
          action={canShowMore ? <button type="button" className="ops-btn ops-btn--sm" onClick={() => setLimit(MAX_LIMIT)}>Show older runs</button> : null}
          foot={rows && rows.length > 0 ? <Pager {...pagerProps} label="runs" /> : null}>
          {!rows ? <Skeleton rows={6} /> : rows.length === 0 ? <Empty title="No runs yet">The first entry appears after the next nightly run at 3:00 AM Detroit time, or after you press Run cleanup now.</Empty> : (
            <div className="ops-table-wrap">
              <table className="ops-table">
                <thead><tr>
                  <th>Ran</th><th>Trigger</th><th>Mode</th>
                  <th className="num">Mailboxes</th><th className="num">Inbox</th><th className="num">Sent</th><th className="num">Trash</th>
                  <th className="num">Deleted</th><th className="num">Failed</th>
                  <th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {pageRows.map(r => (
                    <tr key={r.runId} className={`is-clickable${openId === r.runId ? ' is-selected' : ''}`} onClick={() => openRun(r)}>
                      <td><div className="ops-cell-main">{fmtDateTime(r.startedAt)}</div>{r.durationMs != null && <div className="ops-cell-sub">{fmtDuration(r.durationMs)}</div>}</td>
                      <td>{triggerLabel(r)}</td>
                      <td>{modeLabel(r)}</td>
                      <td className="num">{r.usersTotal}</td>
                      <td className="num">{r.inbox}</td>
                      <td className="num">{r.sent}</td>
                      <td className="num">{r.trash}</td>
                      <td className="num"><strong>{r.deleted}</strong></td>
                      <td className={`num${r.failed > 0 ? ' neg' : ''}`}>{r.failed}</td>
                      <td><StatusPill status={r.status} /></td>
                      <td onClick={e => e.stopPropagation()}>
                        <button type="button" className="ops-btn ops-btn--sm" onClick={() => openRun(r)}>Open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
      {openId && (
        <>
          <div className="ops-drawer__backdrop" onClick={closeRun} />
          <aside className="ops-drawer ops-drawer--wide" role="dialog" aria-modal="true" aria-label="Mailbox cleanup run">
            <div className="ops-drawer__head">
              <div>
                <div className="ops-label">Mailbox cleanup run</div>
                <h2 style={{ margin: '4px 0 2px' }}>{detail ? fmtDateTime(detail.startedAt) : 'Run'}</h2>
                <div className="ops-cell-sub">{detail ? `${triggerLabel(detail)} · ${modeLabel(detail)}` : ''}</div>
              </div>
              <button type="button" className="ops-close" onClick={closeRun} aria-label="Close"><Icon name="x" /></button>
            </div>
            <div className="ops-drawer__body">
              {!detail && (rows ? <div className="ops-error">This run is not in the loaded list. Use Show older runs or Refresh.</div> : <Skeleton rows={5} />)}
              {detail && (
                <>
                  <dl className="ops-dl ops-dl--4">
                    <div><dt>Status</dt><dd><StatusPill status={detail.status} /></dd></div>
                    <div><dt>Mode</dt><dd>{modeLabel(detail)}</dd></div>
                    <div><dt>Started by</dt><dd>{detail.requestedBy || triggerLabel(detail)}</dd></div>
                    <div><dt>Scope</dt><dd>{retentionLabel(detail)}</dd></div>
                    <div><dt>Mailboxes</dt><dd>{detail.usersTotal}{detail.mailDomain ? <div className="ops-cell-sub">on {detail.mailDomain}</div> : null}</dd></div>
                    <div><dt>Inbox</dt><dd>{detail.inbox}</dd></div>
                    <div><dt>Sent</dt><dd>{detail.sent}</dd></div>
                    <div><dt>Trash</dt><dd>{detail.trash}</dd></div>
                    <div><dt>Deleted</dt><dd><strong>{detail.deleted}</strong></dd></div>
                    <div><dt>Failed</dt><dd style={detail.failed > 0 ? { color: 'var(--red)', fontWeight: 600 } : undefined}>{detail.failed}</dd></div>
                    <div><dt>Alert</dt><dd>{detail.alertSent ? 'Failure email sent' : 'None needed'}</dd></div>
                    <div><dt>Finished</dt><dd>{detail.finishedAt ? fmtDateTime(detail.finishedAt) : <span className="muted">Not recorded</span>}{detail.durationMs != null && <div className="ops-cell-sub">{fmtDuration(detail.durationMs)}</div>}</dd></div>
                  </dl>
                  {detail.dryRun && <div className="ops-note">Dry run: the counts show messages that would have been deleted. Nothing was removed.</div>}
                  {detail.failed > 0 && <div className="ops-note ops-note--review">Some mailboxes could not be cleaned. The errors below are also in the failure email and the hca-mail-cleanup-cleanup logs in CloudWatch.</div>}
                  <div className="ops-table-wrap">
                    <table className="ops-table">
                      <thead><tr><th>Mailbox</th><th className="num">Inbox</th><th className="num">Sent</th><th className="num">Trash</th><th>Outcome</th></tr></thead>
                      <tbody>
                        {(detail.mailboxes || []).length === 0 ? (
                          <tr><td colSpan={5}><span className="muted">No mailboxes were processed.</span></td></tr>
                        ) : detail.mailboxes.map((m, i) => (
                          <tr key={`${m.user || 'mailbox'}-${i}`}>
                            <td className="ops-cell-main">{m.user}</td>
                            <td className="num">{m.status === 'failed' ? '' : m.inbox}</td>
                            <td className="num">{m.status === 'failed' ? '' : m.sent}</td>
                            <td className="num">{m.status === 'failed' ? '' : m.trash}</td>
                            <td><StatusPill status={m.status} />{m.error ? <div className="ops-cell-sub neg">{m.error}</div> : null}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="ops-drawer__foot">
              <span className="muted">Runs are started by the nightly schedule in AWS or by Run cleanup now.</span>
              <span className="ops-actions">
                <button type="button" className="ops-btn" onClick={closeRun}>Close</button>
              </span>
            </div>
          </aside>
        </>
      )}
    </main>
  );
}
